'use client'

import { captureClientError } from '@lib/capture-client-error'
import { LOGIN_PATH, NO_TOKEN_TO_REFRESH_ERROR } from '@lib/constants'
import { SharedTRPCProvider } from '@packages/utilities/hooks'
import {
  createDexiePersister,
  createQueryString,
  isBadGateway,
  isErrorKey,
  isUnauthorised,
  Time,
} from '@packages/utilities/utils'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import {
  TRPCClientError as BaseTRPCClientError,
  httpBatchStreamLink,
  httpLink,
  httpSubscriptionLink,
  loggerLink,
  splitLink,
} from '@trpc/client'
import { createTRPCReact } from '@trpc/react-query'
import { TRPC_ERROR_CODES_BY_KEY } from '@trpc/server/unstable-core-do-not-import'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { environment } from 'src/environment'
import { z } from 'zod'
import type { AppRouter } from '../server/api/root'
import { getUrl, transformer } from './shared'

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>()

export const api = createTRPCReact<AppRouter>({
  abortOnUnmount: true,
})

export type TRPCClientError = NonNullable<
  ReturnType<
    // Actual mutation does not matter, they all have the same error type.
    typeof api.auth.login.useMutation
  >['error']
>

interface Properties {
  NODE_ENV: string
  children: React.ReactNode
  styleNonce: string | undefined
}

const ignorableMutationErrorMessages = new Set<string>([
  NO_TOKEN_TO_REFRESH_ERROR,
])

const ignoreErrorsSchema = z
  .string()
  .refine(
    (key): key is keyof typeof TRPC_ERROR_CODES_BY_KEY =>
      key in TRPC_ERROR_CODES_BY_KEY
  )
  .array()

export const TRPCReactProvider: React.FC<Properties> = ({
  children,
  NODE_ENV,
  styleNonce,
}) => {
  const router = useRouter()

  const [queryClient] = useState(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          gcTime: new Time({ hours: 1 }).totalMilliseconds,
          staleTime: 0,
          retry: (failureCount, error) => {
            if (failureCount > 3) {
              return false
            }
            return isBadGateway(error)
          },
          refetchOnWindowFocus: false,
        },
        mutations: {
          gcTime: 0,
          retry: false,
        },
      },
      queryCache: new QueryCache({
        onError(error, query) {
          if (query.meta?.errorKeysToIgnore) {
            const errorKeysToIgnore = ignoreErrorsSchema.safeParse(
              query.meta.ignoreErrors
            )
            if (
              errorKeysToIgnore.success &&
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- The return type for this function is just hard to get right.
              errorKeysToIgnore.data.some((key) => isErrorKey(error, key))
            ) {
              return
            }
          } else if (isUnauthorised(error)) {
            queryClient.clear()
            if (window.location.pathname !== LOGIN_PATH) {
              const loginQuery = createQueryString(undefined, [
                { name: 'error', value: error.message },
              ])
              router.replace(`${LOGIN_PATH}?${loginQuery}`)
              router.refresh()
            }
          } else {
            captureClientError(error)
          }
        },
      }),
      mutationCache: new MutationCache({
        onError(error) {
          if (isUnauthorised(error)) {
            queryClient.clear()
            if (window.location.pathname !== LOGIN_PATH) {
              const loginQuery = createQueryString(undefined, [
                { name: 'error', value: error.message },
              ])
              router.replace(`${LOGIN_PATH}?${loginQuery}`)
            }
          } else if (ignorableMutationErrorMessages.has(error.message)) {
            return
          } else {
            captureClientError(error)
          }
        },
        onSettled(data, error, variables, context, mutation) {
          if (!mutation.meta?.skipRefresh) {
            router.refresh()
          }
        },
      }),
    })
  })

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (options) => {
            if (
              options.direction === 'down' &&
              options.result instanceof BaseTRPCClientError &&
              options.result.message.includes('aborted')
            ) {
              return false
            }
            return NODE_ENV === 'development'
          },
        }),
        splitLink({
          condition: (operation) => operation.type === 'subscription',
          true: httpSubscriptionLink({
            url: getUrl(),
            transformer,
          }),
          false: splitLink({
            condition: (operation) =>
              (operation.context.useStream as boolean | undefined) ?? false,
            true: httpBatchStreamLink({
              url: getUrl(),
              transformer,
            }),
            false: httpLink({
              url: getUrl(),
              transformer,
              methodOverride: 'POST',
            }),
          }),
        }),
      ],
    })
  )

  const [persister] = useState(createDexiePersister())

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, buster: environment.NEXT_PUBLIC_BUILD_ID }}
    >
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <api.Provider client={trpcClient} queryClient={queryClient}>
          <SharedTRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            {children}
            <ReactQueryDevtools initialIsOpen={false} styleNonce={styleNonce} />
          </SharedTRPCProvider>
        </api.Provider>
      </TRPCProvider>
    </PersistQueryClientProvider>
  )
}
